import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Plus, X, ChevronLeft, ChevronRight, Users, Clock, MapPin, 
  AlertTriangle, Car, Calendar, Phone, Navigation, Edit2, Trash2,
  Check, Home, DollarSign, Maximize2, GripVertical
} from 'lucide-react';
import { format, addDays, addWeeks, startOfWeek, parseISO, isSameDay } from 'date-fns';
import { formatJobTime, formatDuration, serviceTypes, getGoogleMapsDirectionsUrl } from '../utils/jobData';

const teamColors = ['#2c5f4e', '#b8926a', '#5b7a9d', '#8b5a8b', '#cd853f', '#2563eb', '#dc2626'];

function RouteBuilder({ 
  jobs, 
  employees, 
  allJobs, // All jobs for checking future appointments
  selectedDate,
  onJobClick, 
  onUpdateJob,
  onOpenReschedule,
  routeDriveTimes = {}
}) {
  // Routes state - fresh each day, not persisted
  const [routes, setRoutes] = useState([]);
  const [showNewRoute, setShowNewRoute] = useState(false);
  const [newRouteName, setNewRouteName] = useState('');
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(null);
  const [showScheduleNext, setShowScheduleNext] = useState(null);
  const [scheduleNextDate, setScheduleNextDate] = useState(null);
  const [scheduleNextTime, setScheduleNextTime] = useState('09:00');
  const [scheduleNextWeekStart, setScheduleNextWeekStart] = useState(null);
  
  // For mobile swipe
  const columnsRef = useRef(null);

  // Enrich jobs that have a team assigned but are missing routeId
  const enrichedJobs = useMemo(() => {
    return jobs.map(job => {
      if (!job.routeId && job.team && job.team.length > 0) {
        const sortedIds = [...job.team].sort();
        return {
          ...job,
          routeId: `team_${sortedIds.join('_')}`,
          routeName: (job.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || null
        };
      }
      return job;
    });
  }, [jobs]);

  // Derive route team from the jobs assigned to it
  const deriveRouteTeam = (routeId) => {
    const routeJobs = enrichedJobs.filter(j => j.routeId === routeId && j.team && j.team.length > 0);
    if (routeJobs.length === 0) return [];
    const job = routeJobs[0];
    return (job.team || []).map((empId, i) => ({ empId, isPrimary: i === 0 }));
  };

  // Sync routes with jobs: add routes for new routeIds, remove auto-generated routes that have no jobs
  useEffect(() => {
    const jobRouteIds = new Set(enrichedJobs.filter(j => j.routeId).map(j => j.routeId));

    setRoutes(prevRoutes => {
      // Keep routes that have jobs OR were manually created (id starts with "route-")
      const keptRoutes = prevRoutes.filter(r => jobRouteIds.has(r.id) || r.id.startsWith('route-'));

      // Backfill team from job data for any kept route that has no team
      const backfilledRoutes = keptRoutes.map(r => {
        if (r.team && r.team.length > 0) return r;
        const derived = deriveRouteTeam(r.id);
        return derived.length > 0 ? { ...r, team: derived } : r;
      });

      // Add routes for routeIds we don't have yet
      const currentRouteIds = new Set(backfilledRoutes.map(r => r.id));
      const newRoutes = [...jobRouteIds]
        .filter(routeId => !currentRouteIds.has(routeId))
        .map((routeId, idx) => ({
          id: routeId,
          name: `Route ${String.fromCharCode(65 + backfilledRoutes.length + idx)}`,
          team: deriveRouteTeam(routeId),
          color: teamColors[(backfilledRoutes.length + idx) % teamColors.length]
        }));

      const updatedRoutes = [...backfilledRoutes, ...newRoutes];
      // Only update state if routes actually changed (check id + team length)
      if (updatedRoutes.length !== prevRoutes.length ||
          updatedRoutes.some((r, i) => r.id !== prevRoutes[i]?.id || r.team.length !== (prevRoutes[i]?.team?.length || 0))) {
        return updatedRoutes;
      }
      return prevRoutes;
    });
  }, [enrichedJobs]);

  // Active employees (includes onboarding new hires so they appear on dispatch)
  const activeEmployees = useMemo(() =>
    employees.filter(emp => emp.status === 'active' || emp.status === 'onboarding').map((emp, idx) => ({
      ...emp,
      displayName: `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.`,
      color: teamColors[idx % teamColors.length]
    })),
    [employees]
  );

  // Get jobs for a route
  const getRouteJobs = (routeId) => {
    return enrichedJobs
      .filter(job => job.routeId === routeId)
      .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''));
  };

  // Unassigned jobs (no routeId)
  const unassignedJobs = useMemo(() =>
    enrichedJobs
      .filter(job => !job.routeId)
      .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || '')),
    [enrichedJobs]
  );

  // Check if client has future appointments
  const clientHasFutureJobs = (clientId, currentJobDate) => {
    if (!allJobs || !clientId) return true;
    return allJobs.some(job => 
      job.clientId === clientId && 
      job.date > currentJobDate &&
      job.status !== 'cancelled'
    );
  };

  // Calculate route totals
  const getRouteTotals = (routeJobs) => ({
    jobs: routeJobs.length,
    hours: routeJobs.reduce((sum, j) => sum + (j.duration || 0), 0),
    revenue: routeJobs.reduce((sum, j) => sum + (j.price || 0), 0),
    sqft: routeJobs.reduce((sum, j) => sum + (j.sqft || 0), 0)
  });

  // Get team member info
  const getEmployeeById = (empId) => activeEmployees.find(e => e.id === empId);

  // Move job to different route
  const moveJob = async (jobId, direction) => {
    const job = enrichedJobs.find(j => j.id === jobId);
    if (!job) return;

    const routeIds = [null, ...routes.map(r => r.id)];
    const currentIndex = routeIds.indexOf(job.routeId || null);

    let newIndex;
    if (direction === 'left') {
      newIndex = currentIndex - 1;
      if (newIndex < 0) newIndex = routeIds.length - 1;
    } else {
      newIndex = currentIndex + 1;
      if (newIndex >= routeIds.length) newIndex = 0;
    }

    const newRouteId = routeIds[newIndex];
    const newRoute = routes.find(r => r.id === newRouteId);
    const update = {
      routeId: newRouteId,
      routeName: newRoute?.name || null
    };

    // Only update team fields when moving to a route that has a team assigned
    if (newRoute?.team?.length > 0) {
      update.team = newRoute.team.map(t => t.empId);
      update.teamNames = newRoute.team.map(t => {
        const emp = employees.find(e => e.id === t.empId);
        return emp ? `${emp.firstName} ${emp.lastName?.charAt(0)}.` : '';
      }).filter(Boolean);
      update.primaryTeamMember = newRoute.team.find(t => t.isPrimary)?.empId || null;
    } else if (newRoute && job.team && job.team.length > 0) {
      // Target route has no team — carry the job's team to the route header
      const jobTeam = (job.team || []).map((empId, i) => ({ empId, isPrimary: i === 0 }));
      setRoutes(prev => prev.map(r => r.id === newRoute.id ? { ...r, team: jobTeam } : r));
    }
    // When moving to unassigned (null route), preserve existing team on the job

    await onUpdateJob(jobId, update);
  };

  // Add new route
  const addRoute = () => {
    if (!newRouteName.trim()) return;
    
    const newRoute = {
      id: `route-${Date.now()}`,
      name: newRouteName.trim(),
      team: [], // { empId, isPrimary }
      color: teamColors[routes.length % teamColors.length]
    };
    
    setRoutes([...routes, newRoute]);
    setNewRouteName('');
    setShowNewRoute(false);
  };

  // Update route name
  const updateRouteName = (routeId, newName) => {
    setRoutes(routes.map(r => 
      r.id === routeId ? { ...r, name: newName } : r
    ));
    setEditingRouteId(null);
  };

  // Toggle team member on route
  const toggleTeamMember = (routeId, empId) => {
    setRoutes(routes.map(r => {
      if (r.id !== routeId) return r;
      
      const existingIndex = r.team.findIndex(t => t.empId === empId);
      if (existingIndex >= 0) {
        // Remove
        return { ...r, team: r.team.filter(t => t.empId !== empId) };
      } else {
        // Add - if first member, make primary
        const isPrimary = r.team.length === 0;
        return { ...r, team: [...r.team, { empId, isPrimary }] };
      }
    }));
  };

  // Set primary team member
  const setPrimaryMember = (routeId, empId) => {
    setRoutes(routes.map(r => {
      if (r.id !== routeId) return r;
      return {
        ...r,
        team: r.team.map(t => ({ ...t, isPrimary: t.empId === empId }))
      };
    }));
  };

  // Save team assignments to all jobs in a route
  const saveTeamAssignments = async (routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (!route) return;

    const routeJobs = getRouteJobs(routeId);
    const teamMemberIds = route.team.map(t => t.empId);
    const teamNames = route.team.map(t => {
      const emp = employees.find(e => e.id === t.empId);
      return emp ? `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.` : '';
    }).filter(Boolean);
    const primaryMemberId = route.team.find(t => t.isPrimary)?.empId || null;

    // Update each job with the team assignment — never wipe existing team
    for (const job of routeJobs) {
      if (teamMemberIds.length === 0 && job.team && job.team.length > 0) continue;
      await onUpdateJob(job.id, {
        team: teamMemberIds,
        teamNames,
        primaryTeamMember: primaryMemberId,
        routeId: routeId,
        routeName: route.name
      });
    }

    setShowTeamModal(null);
  };

  // Delete route (moves jobs back to unassigned but preserves team assignment)
  const deleteRoute = async (routeId) => {
    const routeJobs = getRouteJobs(routeId);
    for (const job of routeJobs) {
      await onUpdateJob(job.id, {
        routeId: null,
        routeName: null
      });
    }
    setRoutes(routes.filter(r => r.id !== routeId));
  };

  // Open Schedule Next modal
  const openScheduleNext = (job) => {
    const frequency = job.serviceType;
    let daysToAdd = 14; // Default biweekly
    if (frequency === 'weekly') daysToAdd = 7;
    if (frequency === 'monthly') daysToAdd = 28;
    if (frequency === 'one_time' || frequency === 'deep_clean') daysToAdd = 14;

    const suggestedDate = addDays(parseISO(job.date), daysToAdd);
    setScheduleNextDate(format(suggestedDate, 'yyyy-MM-dd'));
    setScheduleNextTime(job.scheduledTime || '09:00');
    setScheduleNextWeekStart(startOfWeek(suggestedDate, { weekStartsOn: 0 }));
    setShowScheduleNext(job);
  };

  // Get jobs for schedule next preview
  const getWeekJobsPreview = (weekStart) => {
    if (!allJobs || !weekStart) return [];
    const weekEnd = addDays(weekStart, 7);
    return allJobs.filter(job => {
      const jobDate = parseISO(job.date);
      return jobDate >= weekStart && jobDate < weekEnd;
    });
  };

  // Handle drag and drop
  const handleDragStart = (e, job) => {
    e.dataTransfer.setData('jobId', job.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetRouteId) => {
    e.preventDefault();
    const jobId = e.dataTransfer.getData('jobId');
    if (jobId) {
      const job = enrichedJobs.find(j => j.id === jobId);
      const targetRoute = routes.find(r => r.id === targetRouteId);
      const update = {
        routeId: targetRouteId,
        routeName: targetRoute?.name || null
      };

      // Only update team fields when dropping onto a route that has a team assigned
      if (targetRoute?.team?.length > 0) {
        update.team = targetRoute.team.map(t => t.empId);
        update.teamNames = targetRoute.team.map(t => {
          const emp = employees.find(e => e.id === t.empId);
          return emp ? `${emp.firstName} ${emp.lastName?.charAt(0)}.` : '';
        }).filter(Boolean);
        update.primaryTeamMember = targetRoute.team.find(t => t.isPrimary)?.empId || null;
      } else if (targetRoute && job?.team && job.team.length > 0) {
        // Target route has no team — carry the job's team to the route header
        const jobTeam = (job.team || []).map((empId, i) => ({ empId, isPrimary: i === 0 }));
        setRoutes(prev => prev.map(r => r.id === targetRoute.id ? { ...r, team: jobTeam } : r));
      }
      // When dropping to unassigned (null route), preserve existing team on the job

      await onUpdateJob(jobId, update);
    }
  };

  // Get drive time between jobs
  const getDriveTime = (jobId) => {
    const driveInfo = routeDriveTimes[jobId];
    return driveInfo?.duration || null;
  };

  return (
    <div className="route-builder">
      {/* Route Columns Container */}
      <div className="route-columns-wrapper" ref={columnsRef}>
        <div className="route-columns">
          
          {/* Unassigned Column */}
          <div 
            className="route-column unassigned"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, null)}
          >
            <div className="column-header">
              <div className="column-title-row">
                <span className="column-icon">📋</span>
                <h3>Unassigned</h3>
                <span className="job-count">{unassignedJobs.length}</span>
              </div>
            </div>
            
            <div className="column-jobs">
              {unassignedJobs.map((job, index) => (
                <JobCard
                  key={job.id}
                  job={job}
                  index={index}
                  isUnassigned
                  needsRebooking={!clientHasFutureJobs(job.clientId, job.date)}
                  driveTime={getDriveTime(job.id)}
                  onJobClick={onJobClick}
                  onMove={moveJob}
                  onReschedule={() => onOpenReschedule(job)}
                  onScheduleNext={() => openScheduleNext(job)}
                  onDragStart={handleDragStart}
                />
              ))}
              {unassignedJobs.length === 0 && (
                <div className="empty-column">
                  <Check size={24} />
                  <p>All jobs assigned!</p>
                </div>
              )}
            </div>
          </div>

          {/* Route Columns */}
          {routes.map((route, routeIndex) => {
            const routeJobs = getRouteJobs(route.id);
            const totals = getRouteTotals(routeJobs);
            const primaryMember = route.team.find(t => t.isPrimary);
            const helperMembers = route.team.filter(t => !t.isPrimary);

            return (
              <div 
                key={route.id}
                className="route-column"
                style={{ '--route-color': route.color }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, route.id)}
              >
                <div className="column-header">
                  <div className="column-title-row">
                    <span className="route-letter" style={{ background: route.color }}>
                      {String.fromCharCode(65 + routeIndex)}
                    </span>
                    {editingRouteId === route.id ? (
                      <input
                        className="route-name-input"
                        value={route.name}
                        onChange={(e) => setRoutes(routes.map(r => 
                          r.id === route.id ? { ...r, name: e.target.value } : r
                        ))}
                        onBlur={() => setEditingRouteId(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setEditingRouteId(null)}
                        autoFocus
                      />
                    ) : (
                      <h3 onClick={() => setEditingRouteId(route.id)}>{route.name}</h3>
                    )}
                    <button 
                      className="btn-icon-sm delete-route"
                      onClick={() => deleteRoute(route.id)}
                      title="Delete route"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Team Assignment */}
                  <div className="team-section">
                    <button 
                      className="team-assign-btn"
                      onClick={() => setShowTeamModal(route.id)}
                    >
                      <Users size={14} />
                      {route.team.length === 0 ? (
                        <span>Assign Team</span>
                      ) : (
                        <span>
                          {primaryMember && getEmployeeById(primaryMember.empId)?.displayName}
                          {helperMembers.length > 0 && ` +${helperMembers.length}`}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="column-stats">
                    <span className="stat">{totals.jobs} jobs</span>
                    <span className="stat">{totals.hours}h</span>
                    {totals.sqft > 0 && <span className="stat">{totals.sqft.toLocaleString()} sqft</span>}
                    <span className="stat revenue">${totals.revenue}</span>
                  </div>
                </div>

                <div className="column-jobs">
                  {routeJobs.map((job, index) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      index={index}
                      routeColor={route.color}
                      needsRebooking={!clientHasFutureJobs(job.clientId, job.date)}
                      driveTime={getDriveTime(job.id)}
                      onJobClick={onJobClick}
                      onMove={moveJob}
                      onReschedule={() => onOpenReschedule(job)}
                      onScheduleNext={() => openScheduleNext(job)}
                      onDragStart={handleDragStart}
                    />
                  ))}
                  {routeJobs.length === 0 && (
                    <div className="empty-column">
                      <p>Drop jobs here</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add Route Column */}
          {showNewRoute ? (
            <div className="route-column new-route">
              <div className="new-route-form">
                <input
                  className="new-route-input"
                  placeholder="Route name..."
                  value={newRouteName}
                  onChange={(e) => setNewRouteName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRoute()}
                  autoFocus
                />
                <div className="new-route-actions">
                  <button className="btn btn-primary btn-sm" onClick={addRoute}>
                    Create
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    setShowNewRoute(false);
                    setNewRouteName('');
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button className="add-route-btn" onClick={() => setShowNewRoute(true)}>
              <Plus size={24} />
              <span>Add Route</span>
            </button>
          )}
        </div>
      </div>

      {/* Team Assignment Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(null)}>
          <div className="team-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Team</h3>
              <button className="btn-icon" onClick={() => setShowTeamModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-hint">Select team members for this route. Click "Save & Assign" to update all jobs in this route so team members can see them in their app.</p>
              <div className="team-list">
                {activeEmployees.map(emp => {
                  const route = routes.find(r => r.id === showTeamModal);
                  const teamMember = route?.team.find(t => t.empId === emp.id);
                  const isAssigned = !!teamMember;
                  const isPrimary = teamMember?.isPrimary;

                  return (
                    <div key={emp.id} className={`team-member-row ${isAssigned ? 'assigned' : ''}`}>
                      <button
                        className={`team-toggle ${isAssigned ? 'active' : ''}`}
                        style={{ 
                          background: isAssigned ? emp.color : '#f3f4f6',
                          color: isAssigned ? 'white' : '#6b7280'
                        }}
                        onClick={() => toggleTeamMember(showTeamModal, emp.id)}
                      >
                        {emp.displayName}
                      </button>
                      {isAssigned && (
                        <button
                          className={`primary-toggle ${isPrimary ? 'is-primary' : ''}`}
                          onClick={() => setPrimaryMember(showTeamModal, emp.id)}
                        >
                          {isPrimary ? '★ Primary' : 'Set Primary'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTeamModal(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => saveTeamAssignments(showTeamModal)}>
                <Check size={16} /> Save & Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Next Modal */}
      {showScheduleNext && (
        <div className="modal-overlay" onClick={() => setShowScheduleNext(null)}>
          <div className="schedule-next-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Schedule Next Appointment</h3>
              <button className="btn-icon" onClick={() => setShowScheduleNext(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="schedule-next-client">
                <strong>{showScheduleNext.clientName}</strong>
                <span>{showScheduleNext.address}</span>
                <span className="frequency-badge">
                  {serviceTypes[showScheduleNext.serviceType]?.label || showScheduleNext.serviceType}
                </span>
              </div>

              {/* Week Navigation */}
              <div className="week-nav">
                <button 
                  className="btn-icon"
                  onClick={() => setScheduleNextWeekStart(addWeeks(scheduleNextWeekStart, -1))}
                >
                  <ChevronLeft size={20} />
                </button>
                <span>{format(scheduleNextWeekStart, 'MMMM yyyy')}</span>
                <button 
                  className="btn-icon"
                  onClick={() => setScheduleNextWeekStart(addWeeks(scheduleNextWeekStart, 1))}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Week Calendar */}
              <div className="week-calendar">
                {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                  const date = addDays(scheduleNextWeekStart, dayOffset);
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const isSelected = scheduleNextDate === dateStr;
                  const dayJobs = getWeekJobsPreview(scheduleNextWeekStart)
                    .filter(j => j.date === dateStr);

                  return (
                    <button
                      key={dayOffset}
                      className={`day-cell ${isSelected ? 'selected' : ''}`}
                      onClick={() => setScheduleNextDate(dateStr)}
                    >
                      <span className="day-name">{format(date, 'EEE')}</span>
                      <span className="day-num">{format(date, 'd')}</span>
                      {dayJobs.length > 0 && (
                        <span className="day-jobs">{dayJobs.length} jobs</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Time Selection */}
              <div className="time-selection">
                <label>Time</label>
                <input
                  type="time"
                  value={scheduleNextTime}
                  onChange={(e) => setScheduleNextTime(e.target.value)}
                />
              </div>

              {/* Preview of selected day */}
              {scheduleNextDate && (
                <div className="day-preview">
                  <h4>{format(parseISO(scheduleNextDate), 'EEEE, MMMM d')}</h4>
                  <div className="day-preview-jobs">
                    {getWeekJobsPreview(scheduleNextWeekStart)
                      .filter(j => j.date === scheduleNextDate)
                      .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''))
                      .map(job => (
                        <div key={job.id} className="preview-job">
                          <span className="preview-time">{formatJobTime(job.scheduledTime)}</span>
                          <span className="preview-name">{job.clientName}</span>
                        </div>
                      ))
                    }
                    {getWeekJobsPreview(scheduleNextWeekStart)
                      .filter(j => j.date === scheduleNextDate).length === 0 && (
                      <p className="no-jobs-preview">No jobs scheduled</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowScheduleNext(null)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  // This would create the next job
                  alert(`Would create job for ${showScheduleNext.clientName} on ${scheduleNextDate} at ${scheduleNextTime}`);
                  setShowScheduleNext(null);
                }}
              >
                <Calendar size={16} /> Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Job Card Component
function JobCard({ 
  job, 
  index, 
  isUnassigned, 
  routeColor, 
  needsRebooking,
  driveTime,
  onJobClick, 
  onMove, 
  onReschedule,
  onScheduleNext,
  onDragStart 
}) {
  const formatTime = (time) => {
    if (!time) return '--';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '--';
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration * 60;
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = Math.round(totalMinutes % 60);
    return formatTime(`${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`);
  };

  return (
    <div
      className={`rb-job-card ${isUnassigned ? 'unassigned' : ''} ${needsRebooking ? 'needs-rebooking' : ''}`}
      style={{ '--card-color': isUnassigned ? '#dc2626' : routeColor }}
      draggable
      onDragStart={(e) => onDragStart(e, job)}
      onClick={() => onJobClick(job)}
    >
      {/* Drive time indicator */}
      {driveTime && index > 0 && (
        <div className="rb-drive-time-badge">
          <Car size={10} /> {driveTime} min
        </div>
      )}

      {/* Needs Rebooking Flag */}
      {needsRebooking && (
        <div className="rb-rebooking-flag">
          <AlertTriangle size={12} /> Needs Rebooking
        </div>
      )}

      {/* Time - Prominent */}
      <div className="rb-job-time-section">
        <div className="rb-time-main">{formatTime(job.scheduledTime)}</div>
        <div className="rb-time-meta">
          → {getEndTime(job.scheduledTime, job.duration)} • {job.duration}h
        </div>
      </div>

      {/* Job Number */}
      {!isUnassigned && (
        <div className="rb-job-number" style={{ background: routeColor }}>
          {index + 1}
        </div>
      )}

      {/* Client Info */}
      <div className="rb-job-client">
        <div className="rb-client-name">{job.clientName}</div>
        <div className="rb-client-address">
          <MapPin size={12} /> {job.address}{job.city ? `, ${job.city}` : ''}
        </div>
      </div>

      {/* Details Row */}
      <div className="rb-job-details-row">
        <span className="rb-service-type">{serviceTypes[job.serviceType]?.label || job.serviceType}</span>
        {job.sqft && <span className="rb-sqft">{job.sqft.toLocaleString()} sqft</span>}
        <span className="rb-price">${job.price}</span>
      </div>

      {/* Actions */}
      <div className="rb-job-actions-row">
        <div className="rb-move-btns">
          <button 
            className="rb-move-btn" 
            onClick={(e) => { e.stopPropagation(); onMove(job.id, 'left'); }}
            title="Move left"
          >
            ←
          </button>
          <button 
            className="rb-move-btn" 
            onClick={(e) => { e.stopPropagation(); onMove(job.id, 'right'); }}
            title="Move right"
          >
            →
          </button>
        </div>
        <div className="rb-quick-actions">
          <button 
            className="rb-quick-action-btn"
            onClick={(e) => { e.stopPropagation(); onReschedule(); }}
            title="Reschedule"
          >
            <Edit2 size={14} />
          </button>
          <button 
            className="rb-quick-action-btn primary"
            onClick={(e) => { e.stopPropagation(); onScheduleNext(); }}
            title="Schedule Next"
          >
            <Calendar size={14} /> Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default RouteBuilder;
